import React, {useEffect, useMemo, useState} from 'react'
import {
	Box,
	Button,
	Container, Divider, FormControlLabel,
	IconButton,
	InputAdornment,
	Link, Switch,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TextField,
	Typography,
	useTheme
} from '@mui/material'
import {
	AddBoxOutlined,
	AutoFixHighOutlined,
	CompareArrows,
	Edit,
	HighlightOff,
	IndeterminateCheckBoxOutlined
} from '@mui/icons-material'
import CheckIcon                                                                          from '@mui/icons-material/Check'
import CloseIcon from '@mui/icons-material/Close'
import ChevronLeft from '@mui/icons-material/ChevronLeft'
import AudioPreviewPlayer from './AudioPreviewPlayer'
import {useOutletContext} from 'react-router-dom'
import ConfirmDialog from '../ConfirmDialog'
import RegExpTemplates from './RegExpTemplates'
import LeftRight from '../LeftRight'
import {doGet, doPost} from '../../util/do-fetch'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import UndoIcon from '@mui/icons-material/Undo'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import {Collapse} from '@mui/material'
import {doDelete} from '../../util/do-fetch'
import TypeIt                        from "../prepkit/chat/TypeIt"
import {fetchTTSModels, fetchVoices} from "../../util/admin"

/* ---------------- regex helpers ---------------- */

const parseRegExp = (expr) => {
	if (!expr) return null
	const match = expr.match(/^\/(.*)\/([igmus]*)$/)
	return match ? new RegExp(match[1], match[2]) : new RegExp(expr)
}

const highlightMatches = (text, regex) => {
	if (!regex || !text) return text
	return text.replace(withGlobal(regex), m =>
		`<span style="background: yellow; color: black">${m}</span>`
	)
}

const withGlobal = (r) => {
	return r.global ? r : new RegExp(r.source, r.flags + 'g')
}

/* ---------------- main component ---------------- */

const ProductTts = () => {
	const {product} = useOutletContext()
	const theme = useTheme()

	const [tts, setTts] = useState({})
	const [searchTerm, setSearchTerm] = useState('')
	const [replaceWith, setReplaceWith] = useState('')
	const [showTemplates, setShowTemplates] = useState(false)
	const [showFindReplace, setShowFindReplace] = useState(false)
	const [showOnlyModified, setShowOnlyModified] = useState(false)
	const [expandAll, setExpandAll] = useState(false)
	const [confirmReplace, setConfirmReplace] = useState(false)
	const [ttsModel, setTtsModel] = useState('gpt-4o-mini-tts')
	const [voiceId, setVoiceId] = useState('echo')
	const [ttsModels, setTtsModels] = useState([])
	const [voices, setVoices] = useState([])

	useEffect(() => {
		const fetchModels = async () => {
			const tts = await fetchTTSModels()
			const voices = await fetchVoices()
			setTtsModels(tts)
			setVoices(voices)
		}

		fetchModels()
	}, [])

	useEffect(() => {
		if (product?.tts_model) setTtsModel(product.tts_model)
		if (product?.voice_id) setVoiceId(product.voice_id)
	}, [product?.tts_model, product?.voice_id])

	const regex = useMemo(() => {
		try {
			return searchTerm ? parseRegExp(searchTerm) : null
		} catch {
			return null
		}
	}, [searchTerm])

	/* ---------- load TTS when product changes ---------- */

	useEffect(() => {
		if (!product?.id) {
			setTts({})
			return
		}

		const loadTts = async () => {
			const response = await doGet(`/admin/tts?product_id=${product.id}`)
			const rows = await response.json()

			const map = {}
			rows?.forEach(r => {
				if (r.question_id) {
					map[`q:${r.question_id}:${r.field_name}`] = r
				} else {
					map[`t:${r.topic_id}:${r.field_name}`] = r
				}
			})

			setTts(map)
		}

		loadTts()
	}, [product?.id])

	/* ---------- save single TTS ---------- */

	const handleSaveTts = async (topicId, questionId, field, value) => {
		const key = questionId
			? `q:${questionId}:${field}`
			: `t:${topicId}:${field}`

		const existing = Object.values(tts).find(r =>
			r.topic_id === topicId &&
			(r.question_id ?? null) === (questionId ?? null) &&
			r.field_name === field
		)

		const body = {
			product_id: product.id,
			topic_id: topicId,
			question_id: questionId,
			field_name: field,
			tts_text: value
		}

		if (existing?.id) body.id = existing.id

		const options = {
			body: JSON.stringify(body)
		}

		const response = await doPost('/admin/tts', options)
		const saved = await response.json()
		const savedKey = saved.question_id
			? `q:${saved.question_id}:${saved.field_name}`
			: `t:${saved.topic_id}:${saved.field_name}`

		setTts(prev => ({
			...prev,
			[savedKey]: saved
		}))
	}

	/* ---------- replace all ---------- */

	const handleReplaceAll = async () => {
		if (!regex) return

		const makeKey = (topicId, questionId, field) =>
			questionId ? `q:${questionId}:${field}` : `t:${topicId}:${field}`

		const getOverride = (topicId, questionId, field) =>
			tts[makeKey(topicId, questionId, field)]

		const maybeAdd = (arr, topicId, questionId, field, fallback) => {
			const record = getOverride(topicId, questionId, field)
			const currentText = record?.tts_text ?? fallback ?? ''
			if (currentText.search(regex) === -1) return

			arr.push({
				key: makeKey(topicId, questionId, field),
				record,
				payload: {
					id: record?.id,
					product_id: product.id,
					topic_id: topicId,
					question_id: questionId ?? null,
					field_name: field,
					tts_text: currentText.replace(withGlobal(regex), replaceWith || '')
				}
			})
		}

		const updates = []

		product?.topics?.forEach(section => {
			// section name
			maybeAdd(updates, section.id, null, 'section', section.name)

			section?.topics?.forEach(topic => {
				// topic name
				maybeAdd(updates, topic.id, null, 'topic', topic.name)

				topic?.questions?.forEach(q => {
					maybeAdd(updates, topic.id, q.id, 'question', q.question)

					q?.options?.forEach((o, i) => {
						const field = `option-${String.fromCharCode(97 + i)}`
						maybeAdd(updates, topic.id, q.id, field, o.label)
					})

					maybeAdd(updates, topic.id, q.id, 'explanation', q.explanation)
				})
			})
		})

		if (!updates.length) return

		const newTts = { ...tts }

		for (const u of updates) {
			const options = { body: JSON.stringify(u.payload) }
			const response = await doPost('/admin/tts', options)
			const saved = await response.json()
			newTts[u.key] = saved
		}

		setTts(newTts)
	}

	const handleAutoRegexSearchTerm = () => {
		const escapeRegex = searchTerm?.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
		setSearchTerm(escapeRegex)
	}

	/* ---------- render helpers ---------- */

	const renderQuestionRows = (topic) =>
		topic?.questions?.map(q => (
			<React.Fragment key={q.id}>
				<TtsRow
					topicId={topic.id}
					questionId={q.id}
					field="question"
					fallback={q.question}
					regex={regex}
					tts={tts}
					ttsModel={ttsModel}
					voiceId={voiceId}
					product={product}
					forceExpanded={expandAll}
					onlyModified={showOnlyModified}
					onSave={(v) => handleSaveTts(topic.id, q.id, 'question', v)}
					showBreak
				/>

				{q.options.map((o, i) => {
					const field = `option-${String.fromCharCode(97 + i)}`
					return (
						<TtsRow
							key={`${q.id}-${field}`}
							topicId={topic.id}
							questionId={q.id}
							field={field}
							fallback={o.label}
							regex={regex}
							tts={tts}
							ttsModel={ttsModel}
							voiceId={voiceId}
							product={product}
							forceExpanded={expandAll}
							onlyModified={showOnlyModified}
							onSave={(v) => handleSaveTts(topic.id, q.id, field, v)}
						/>
					)
				})}

				<TtsRow
					topicId={topic.id}
					questionId={q.id}
					field="explanation"
					fallback={q.explanation}
					regex={regex}
					tts={tts}
					ttsModel={ttsModel}
					voiceId={voiceId}
					product={product}
					forceExpanded={expandAll}
					onlyModified={showOnlyModified}
					onSave={(v) => handleSaveTts(topic.id, q.id, 'explanation', v)}
				/>
			</React.Fragment>
		))

	const renderSectionRows = (section) => (
		<TtsRow
			topicId={section.id}
			questionId={null}
			field="section"
			fallback={section.name}
			regex={regex}
			tts={tts}
			ttsModel={ttsModel}
			voiceId={voiceId}
			product={product}
			forceExpanded={expandAll}
			onlyModified={showOnlyModified}
			onSave={(v) => handleSaveTts(section.id, null, 'section', v)}
		/>
	)

	const renderTopicRows = (topic) => (
		<TtsRow
			topicId={topic.id}
			questionId={null}
			field="topic"
			fallback={topic.name}
			regex={regex}
			tts={tts}
			ttsModel={ttsModel}
			voiceId={voiceId}
			product={product}
			forceExpanded={expandAll}
			onlyModified={showOnlyModified}
			onSave={(v) => handleSaveTts(topic.id, null, 'topic', v)}
		/>
	)


	/* ---------------- render ---------------- */

	return (
		<Container maxWidth="lg">
			{/*
			<Box position={"fixed"} top={"100px"} bottom={"0px"} left={"0px"} width={"400px"}
			     border={"2px solid black"} padding={"10px"} overflow={"hidden"} background={"white"} fontSize={"10px"}
			     overflow={"scroll"} zIndex={1000}
			>
				<pre>
					{JSON.stringify(tts, null, 2)}
				</pre>
			</Box>
			*/}
			<Box pb={2}>
				{
					showFindReplace &&
					<Box mt={2} p={2} border={`1px solid ${theme.palette.divider}`}>
						<Box display="flex">
							<TextField
								label="Find (text or regex)"
								size="small"
								fullWidth
								value={searchTerm}
								onChange={(e) => setSearchTerm(e.target.value)}
								InputProps={{
									endAdornment: searchTerm && (
										<InputAdornment position="end">
											<Box display={"flex"} flexDirection={"row"}>
												<IconButton onClick={handleAutoRegexSearchTerm}>
													<AutoFixHighOutlined />
												</IconButton>
												<IconButton onClick={() => setSearchTerm('')}>
													<HighlightOff />
												</IconButton>
											</Box>
										</InputAdornment>
									)
								}}
							/>
							<IconButton onClick={() => setSearchTerm(replaceWith)}>
								<ChevronLeft />
							</IconButton>
							<TextField
								label="Replace"
								size="small"
								fullWidth
								value={replaceWith}
								onChange={(e) => setReplaceWith(e.target.value)}
							/>
						</Box>

						<LeftRight
							left={
								<small>
									Regex supported.
									<Link sx={{ml: 0.5, cursor: 'pointer'}} onClick={() => setShowTemplates(true)}>
										View templates
									</Link>
								</small>
							}
							right={
								<Button
									variant="contained"
									disabled={!regex}
									sx={{mt: 1}}
									onClick={() => setConfirmReplace(true)}
								>
									Replace All
								</Button>
							}
						/>

						{showTemplates && (
							<Box mt={2}>
								<RegExpTemplates
									onClose={() => setShowTemplates(false)}
									onClick={(r) => {
										setSearchTerm(r.pattern)
										setShowTemplates(false)
									}}
								/>
							</Box>
						)}

						<ConfirmDialog
							open={confirmReplace}
							title="Confirm Replace All"
							content={
								<Box color={theme.palette.text.primary} minWidth={"400px"}>
									Replace all matches of
									<Box p={1} my={1}
									     border={`1px solid ${theme.palette.divider}`}
									     fontFamily={"monospace"}
									     backgroundColor={theme.palette.action.hover}
									>
										{searchTerm}
									</Box>
									with
									<Box p={1} my={1}
									     border={`1px solid ${theme.palette.divider}`}
									     fontFamily={"monospace"}
									     backgroundColor={theme.palette.action.hover}
									>
										{replaceWith || '[EMPTY]'}
									</Box>
								</Box>
							}
							onConfirm={() => {
								setConfirmReplace(false)
								handleReplaceAll()
							}}
							onCancel={() => setConfirmReplace(false)}
						/>
					</Box>
				}
			</Box>

			<Box display={"flex"} flexDirection={"row"} justifyContent={"flex-end"} alignItems={"center"} >
				<FormControlLabel
					control={<Switch size={"small"} color="secondary" checked={showFindReplace} onChange={(e) => setShowFindReplace(e.target.checked)} />}
					label="Find/Replace"
					sx={{
						whiteSpace: 'nowrap',
						ml: 2,
						'& .MuiFormControlLabel-label': {
							fontSize: 'small'
						}
					}}
				/>
				<FormControlLabel
					control={<Switch size={"small"} color="secondary" checked={showOnlyModified} onChange={(e) => setShowOnlyModified(e.target.checked)} />}
					label="Modified Only"
					sx={{
						whiteSpace: 'nowrap',
						ml: 2,
						'& .MuiFormControlLabel-label': {
							fontSize: 'small'
						}
					}}
				/>
				<FormControlLabel
					control={<Switch size={"small"} color="secondary" checked={expandAll} onChange={(e) => setExpandAll(e.target.checked)} />}
					label="Expand All"
					sx={{
						whiteSpace: 'nowrap',
						ml: 2,
						'& .MuiFormControlLabel-label': {
							fontSize: 'small'
						}
					}}
				/>
			</Box>
			<Divider sx={{my: 2}}/>
			<TableContainer>
				<Table>
					<TableHead>
						<TableRow>
							<TableCell width={140}>Type</TableCell>
							<TableCell colSpan={3}>
								<LeftRight
									left={
										<span>TTS Content</span>
									}
									right={
										<Box display="flex" gap={2} alignItems="center">
											<TextField
												select
												size="small"
												label="TTS Model"
												value={ttsModel}
												onChange={(e) => {
													setTtsModel(e.target.value)
												}}
												SelectProps={{ native: true }}
											>
												<option value="" />
												{ttsModels.map(m => (
													<option key={m.name} value={m.name}>
														{m.name}
													</option>
												))}
											</TextField>

											<TextField
												select
												size="small"
												label="Voice"
												value={voiceId}
												onChange={(e) => setVoiceId(e.target.value)}
												SelectProps={{ native: true }}
											>
												<option value="" />
												{voices.map(v => (
													<option key={v.voice_id} value={v.voice_id}>
														{v.voice_id}
													</option>
												))}
											</TextField>
										</Box>
									}
								/>

							</TableCell>
						</TableRow>
					</TableHead>

					<TableBody>
						{product?.topics.map(section => (
							<React.Fragment key={section.id}>
								{renderSectionRows(section)}
								{section.topics.map(sub => (
									<React.Fragment key={sub.id}>
										{renderTopicRows(sub)}
										{renderQuestionRows(sub)}
									</React.Fragment>
								))}
							</React.Fragment>
						))}
					</TableBody>
				</Table>
			</TableContainer>
		</Container>
	)
}

/* ---------------- row ---------------- */

const TtsRow = ({
	                topicId,
	                questionId,
	                field,
	                fallback,
	                tts,
	                regex,
	                product,
	                ttsModel,
					voiceId,
					forceExpanded,
					onlyModified,
	                onSave,
	                showBreak
                }) => {
	const theme = useTheme()

	const questionKey = `q:${questionId}:${field}`
	const topicKey = `t:${topicId}:${field}`

	const record =
		tts[questionKey] ??
		tts[topicKey]

	const isOverridden = Boolean(record)
	const value = record?.tts_text ?? fallback

	const [edit, setEdit] = useState(false)
	const [draft, setDraft] = useState(value)
	const [showOriginal, setShowOriginal] = useState(false)

	const expanded = useMemo(()=>{
		return showOriginal || forceExpanded
	}, [showOriginal, forceExpanded])

	useEffect(() => {
		setDraft(value)
	}, [value])

	if (regex && !regex.test(value)) return null

	const handleRevert = async () => {
		if (!record?.id) return

		await doDelete(`/admin/tts/${record.id}`)

		// remove from local state by signaling
		// parent already falls back automatically
		delete tts[
			record.question_id
				? questionKey
				: topicKey
			]

		setShowOriginal(false)
	}

	if (onlyModified && !isOverridden) return null

	return (
		<TableRow
			sx={{
				borderTop: showBreak
					? `2px dashed ${
						theme.palette.mode === 'light'
							? theme.palette.common.black
							: theme.palette.common.white
					}`
					: undefined,
				backgroundColor: isOverridden
					? theme.palette.background.default
					: undefined,
				borderLeft: `1px solid ${theme.palette.divider}`,
				borderRight: `1px solid ${theme.palette.divider}`
			}}
		>
			<TableCell>
				<Box display="flex" alignItems="center" gap={0.5}>
					<Box visibility={isOverridden ? 'visible' : 'hidden'}>
						<IconButton
							size="small"
							onClick={() => setShowOriginal(v => !v)}
						>
							{
								expanded
									? <IndeterminateCheckBoxOutlined fontSize="small" color="primary"/>
									: <AddBoxOutlined fontSize="small" color="primary"/>
							}
						</IconButton>
					</Box>
					{field}
				</Box>
			</TableCell>

			<TableCell>
				{edit ? (
					<TextField
						fullWidth
						multiline
						size="small"
						value={draft}
						onChange={e => setDraft(e.target.value)}
					/>
				) : (
					<>
						<Typography
							sx={{
								color: isOverridden
									? theme.palette.primary.main
									: undefined,
								fontWeight: isOverridden ? 500 : undefined
							}}
							dangerouslySetInnerHTML={{
								__html: highlightMatches(value, regex)
							}}
						/>


						<Collapse in={isOverridden && expanded}>
							<Box
								mt={1}
								p={1}
								border={`1px solid ${theme.palette.divider}`}
								borderRadius={1}
								bgcolor={theme.palette.action.hover}
							>
								<Typography
									variant="caption"
									color="text.secondary"
								>
									Original
								</Typography>

								<Typography
									variant="body2"
									sx={{whiteSpace: 'pre-wrap'}}
								>
									{fallback}
								</Typography>

								<Box mt={1}>
									<Button
										startIcon={<UndoIcon />}
										size="small"
										color="error"
										onClick={handleRevert}
									>
										Revert to original
									</Button>
								</Box>
							</Box>
						</Collapse>
					</>
				)}
			</TableCell>

			<TableCell align="center" sx={{ width: 36, p: 0.5 }}>
				{edit ? (
					<>
						<IconButton
							size="small"
							sx={{ p: 0.25 }}
							onClick={() => {
								onSave(draft)
								setEdit(false)
							}}
						>
							<CheckIcon sx={{color: theme.palette.success.main}} />
						</IconButton>
						<IconButton
							size="small"
							sx={{ p: 0.25 }}
							onClick={() => {
								setDraft(value)
								setEdit(false)
							}}
						>
							<CloseIcon sx={{color: theme.palette.error.main}} />
						</IconButton>
					</>
				) : (
					<IconButton
						size="small"
						sx={{ p: 0.25 }}
						size="small"
						onClick={() => setEdit(true)}
					>
						<Edit />
					</IconButton>
				)}
			</TableCell>

			<TableCell sx={{ width: 36, p: 0.5 }}>
				<AudioPreviewPlayer
					tts={edit ? draft : value}
					model={ttsModel}
					voiceId={voiceId}
				/>
			</TableCell>
		</TableRow>
	)
}

export default ProductTts
